/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { DeliveryMethodCreateRequest } from '../models/delivery-method-create-request';
import { DeliveryMethodCreateResponse } from '../models/delivery-method-create-response';
import { DeliveryMethodDetailedResponse } from '../models/delivery-method-detailed-response';
import { DeliveryMethodUpdateRequest } from '../models/delivery-method-update-request';
import { PageDeliveryMethodPageResponse } from '../models/page-delivery-method-page-response';
import { RegionCityWithZipCodeResponse } from '../models/region-city-with-zip-code-response';

/**
 * Delivery method resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class DeliveryMethodsService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getDeliveryMethod
   */
  static readonly GetDeliveryMethodPath =
    '/delivery/methods/{deliveryMethodId}';

  /**
   * Gets delivery method.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDeliveryMethod()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDeliveryMethod$Response(
    params: {
      deliveryMethodId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<DeliveryMethodDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DeliveryMethodsService.GetDeliveryMethodPath,
      'get'
    );
    if (params) {
      rb.path('deliveryMethodId', params.deliveryMethodId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<DeliveryMethodDetailedResponse>;
        })
      );
  }

  /**
   * Gets delivery method.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDeliveryMethod$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDeliveryMethod(
    params: {
      deliveryMethodId: string;
    },
    context?: HttpContext
  ): Observable<DeliveryMethodDetailedResponse> {
    return this.getDeliveryMethod$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<DeliveryMethodDetailedResponse>) =>
          r.body as DeliveryMethodDetailedResponse
      )
    );
  }

  /**
   * Path part for operation updateDeliveryMethod
   */
  static readonly UpdateDeliveryMethodPath =
    '/delivery/methods/{deliveryMethodId}';

  /**
   * Updates a delivery method.
   *
   * Updates an existing delivery method based on its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateDeliveryMethod()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDeliveryMethod$Response(
    params: {
      deliveryMethodId: string;
      body: DeliveryMethodUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DeliveryMethodsService.UpdateDeliveryMethodPath,
      'put'
    );
    if (params) {
      rb.path('deliveryMethodId', params.deliveryMethodId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates a delivery method.
   *
   * Updates an existing delivery method based on its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateDeliveryMethod$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDeliveryMethod(
    params: {
      deliveryMethodId: string;
      body: DeliveryMethodUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateDeliveryMethod$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updateSitesOfDeliveryMethod
   */
  static readonly UpdateSitesOfDeliveryMethodPath =
    '/delivery/methods/{deliveryMethodId}/sites';

  /**
   * Updates sites of a delivery method.
   *
   * Updates sites of an existing delivery method based on its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateSitesOfDeliveryMethod()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSitesOfDeliveryMethod$Response(
    params: {
      deliveryMethodId: string;
      body: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DeliveryMethodsService.UpdateSitesOfDeliveryMethodPath,
      'put'
    );
    if (params) {
      rb.path('deliveryMethodId', params.deliveryMethodId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates sites of a delivery method.
   *
   * Updates sites of an existing delivery method based on its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateSitesOfDeliveryMethod$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateSitesOfDeliveryMethod(
    params: {
      deliveryMethodId: string;
      body: Array<string>;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updateSitesOfDeliveryMethod$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getDeliveryMethods
   */
  static readonly GetDeliveryMethodsPath = '/delivery/methods';

  /**
   * Gets delivery methods.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getDeliveryMethods()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDeliveryMethods$Response(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PageDeliveryMethodPageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DeliveryMethodsService.GetDeliveryMethodsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PageDeliveryMethodPageResponse>;
        })
      );
  }

  /**
   * Gets delivery methods.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getDeliveryMethods$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getDeliveryMethods(
    params?: {
      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PageDeliveryMethodPageResponse> {
    return this.getDeliveryMethods$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PageDeliveryMethodPageResponse>) =>
          r.body as PageDeliveryMethodPageResponse
      )
    );
  }

  /**
   * Path part for operation createDeliveryMethod
   */
  static readonly CreateDeliveryMethodPath = '/delivery/methods';

  /**
   * Creates a new delivery method.
   *
   * Creates a new deliveryMethod and returns the saved resource with its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createDeliveryMethod()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDeliveryMethod$Response(
    params: {
      body: DeliveryMethodCreateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<DeliveryMethodCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DeliveryMethodsService.CreateDeliveryMethodPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<DeliveryMethodCreateResponse>;
        })
      );
  }

  /**
   * Creates a new delivery method.
   *
   * Creates a new deliveryMethod and returns the saved resource with its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createDeliveryMethod$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createDeliveryMethod(
    params: {
      body: DeliveryMethodCreateRequest;
    },
    context?: HttpContext
  ): Observable<DeliveryMethodCreateResponse> {
    return this.createDeliveryMethod$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<DeliveryMethodCreateResponse>) =>
          r.body as DeliveryMethodCreateResponse
      )
    );
  }

  /**
   * Path part for operation getAllZipCodes
   */
  static readonly GetAllZipCodesPath = '/delivery/methods/zip-codes';

  /**
   * Gets all regions' all zip codes.
   *
   * Returns all regions' and all there zips.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllZipCodes()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllZipCodes$Response(
    params?: {},
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<RegionCityWithZipCodeResponse>>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DeliveryMethodsService.GetAllZipCodesPath,
      'get'
    );
    if (params) {
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Array<RegionCityWithZipCodeResponse>>;
        })
      );
  }

  /**
   * Gets all regions' all zip codes.
   *
   * Returns all regions' and all there zips.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllZipCodes$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllZipCodes(
    params?: {},
    context?: HttpContext
  ): Observable<Array<RegionCityWithZipCodeResponse>> {
    return this.getAllZipCodes$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<Array<RegionCityWithZipCodeResponse>>) =>
          r.body as Array<RegionCityWithZipCodeResponse>
      )
    );
  }

  /**
   * Path part for operation deleteDeliveryMethods
   */
  static readonly DeleteDeliveryMethodsPath = '/delivery/methods/selected';

  /**
   * Delete a delivery method.
   *
   * Delete a delivery method by its identifier
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteDeliveryMethods()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteDeliveryMethods$Response(
    params: {
      deliveryMethodIds: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      DeliveryMethodsService.DeleteDeliveryMethodsPath,
      'delete'
    );
    if (params) {
      rb.query('deliveryMethodIds', params.deliveryMethodIds, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Delete a delivery method.
   *
   * Delete a delivery method by its identifier
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteDeliveryMethods$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteDeliveryMethods(
    params: {
      deliveryMethodIds: Array<string>;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.deleteDeliveryMethods$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }
}
